import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Presentational table shell. Behaviour (sorting, selection, paging) lives in
 * `components/data-table`, which drives these elements — this file stays styling-only
 * so a one-off table can use it without pulling in the table engine.
 */

/**
 * `overflow-x-auto` makes the container a scroll container on *both* axes as far as the
 * browser is concerned, so a wheel event over the table was consumed here instead of
 * bubbling to the page — the list would not scroll while the pointer sat on it, which is
 * where the pointer always is.
 *
 * `overscroll-x-contain` keeps horizontal scrolling to this element, while the explicit
 * `overflow-y-visible` stops it from claiming the vertical axis it never needed.
 */
function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto overflow-y-visible overscroll-x-contain"
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom border-separate border-spacing-0 text-sm', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={cn(className)} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn(className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('bg-muted/40 font-medium [&>tr>td]:border-t', className)}
      {...props}
    />
  );
}

/**
 * Hover is a wash, not a highlight: `--accent` is neutral, so crossing a table no
 * longer paints a blue stripe under the pointer. Genuine *selection* is the one row
 * state that still earns the blue.
 */
function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'transition-colors duration-150',
        'hover:bg-accent data-[state=selected]:bg-accent-selected',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The header is the same surface as the body — an opaque `bg-card` only because it has
 * to be able to stick without the rows showing through. What separates it from the data
 * is typography (smaller, lighter, recessive ink) and a single hairline, rather than a
 * grey fill. That one change is most of the difference between "spreadsheet" and
 * "product".
 *
 * Sentence case, not uppercase: these labels are Greek, and `text-transform: uppercase`
 * on Greek is only correct where the engine strips the accents — a detail that is not
 * worth betting a header row on.
 */
function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'bg-card text-muted-soft border-border h-10 border-b px-3 text-left align-middle',
        'text-[11.5px] font-medium tracking-[0.01em] whitespace-nowrap',
        'first:pl-5 last:pr-5 [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * `border-b` here resolves to `--border`, which is close to invisible by design. Rows
 * are separated by the air the padding buys, and the hairline only stops long numeric
 * columns from drifting into each other.
 */
function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'border-border border-b px-3 align-middle first:pl-5 last:pr-5 [&:has([role=checkbox])]:pr-0',
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption data-slot="table-caption" className={cn('text-muted-foreground mt-3 text-sm', className)} {...props} />
  );
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
